// ── FORGE PLATFORM — Vertical Configurations ─────────────────────────────────
// Each vertical defines its own prompts, regulatory context, ingredient options,
// production methods, tab labels, and prohibited ingredients.
// Adding a new vertical = adding one object to this file. Zero app code changes.

export const VERTICALS = {

  // ══ SPIRITS & DISTILLING ════════════════════════════════════════════════════
  spirits: {
    id: "spirits",
    name: "Spirits & Distilling",
    icon: "⚗",
    color: "#C9A227",
    tagline: "Gins, whiskies, rums, liqueurs & more",
    description: "AI-powered spirit formulation with TTB compliance, FDA botanical safety screening, and production protocols for craft distilleries.",
    regulatoryBody: "TTB (Alcohol and Tobacco Tax and Trade Bureau)",
    regulatoryShort: "TTB",
    expertTitle: "master distiller and TTB compliance specialist",
    inputLabel: "Flavor Profile or Ingredients",
    inputPlaceholder: '"Bright citrus, warm ginger, dry finish…" or list your botanicals',
    outputLabel: "Formula",
    tab5Label: "Cocktail Recipes",
    tab1Label: "Formula Workshop",
    categoryLabel: "TTB Category",
    baseLabel: "Base Spirit",

    categories: [
      { value: "", label: "— Let AI suggest best category —" },
      { group: "Whisky / Whiskey" },
      { value: "Bourbon Whisky", label: "Bourbon Whisky" },
      { value: "Rye Whisky", label: "Rye Whisky" },
      { value: "Corn Whisky", label: "Corn Whisky" },
      { value: "Malt Whisky", label: "Malt Whisky" },
      { value: "Wheat Whisky", label: "Wheat Whisky" },
      { value: "Light Whisky", label: "Light Whisky" },
      { value: "American Whisky", label: "American Whisky (other)" },
      { group: "Gin" },
      { value: "Gin", label: "Gin" },
      { value: "American Gin", label: "American Gin" },
      { value: "Dry Gin", label: "Dry Gin (London Dry style)" },
      { group: "Rum / Cane" },
      { value: "Rum", label: "Rum" },
      { value: "Tequila", label: "Tequila" },
      { value: "Mezcal", label: "Mezcal" },
      { group: "Brandy" },
      { value: "Brandy", label: "Brandy (Grape)" },
      { value: "Fruit Brandy", label: "Fruit Brandy" },
      { value: "Applejack", label: "Applejack / Apple Brandy" },
      { group: "Neutral / Vodka" },
      { value: "Vodka", label: "Vodka" },
      { value: "Neutral Spirits", label: "Neutral Spirits" },
      { group: "Flavored / Specialty" },
      { value: "Cordial", label: "Cordial / Liqueur" },
      { value: "Distilled Spirits Specialty", label: "Distilled Spirits Specialty" },
      { value: "Bitters", label: "Bitters" },
      { value: "Aquavit", label: "Aquavit / Akvavit" },
      { value: "Absinthe", label: "Absinthe" },
      { value: "Cream Liqueur", label: "Cream Liqueur" },
    ],

    bases: [
      { value: "", label: "— Let AI suggest best base —" },
      { group: "Grain — Neutral" },
      { value: "190-proof Neutral Grain Spirit (NGS)", label: "190-Proof Neutral Grain Spirit (NGS)" },
      { value: "160-proof Neutral Grain Spirit", label: "160-Proof Neutral Grain Spirit" },
      { value: "Neutral Grain Spirit (80-proof)", label: "Neutral Grain Spirit (80-proof / Vodka base)" },
      { group: "Whiskey Distillate" },
      { value: "New-make Bourbon Distillate", label: "New-Make Bourbon Distillate" },
      { value: "New-make Rye Distillate", label: "New-Make Rye Distillate" },
      { value: "New-make Corn Whiskey Distillate", label: "New-Make Corn Whiskey Distillate" },
      { value: "Aged Bourbon Whiskey", label: "Aged Bourbon Whiskey" },
      { value: "Aged Rye Whiskey", label: "Aged Rye Whiskey" },
      { group: "Brandy / Wine" },
      { value: "Grape Brandy Distillate", label: "Grape Brandy Distillate" },
      { value: "Apple Brandy Distillate", label: "Apple / Pear Brandy Distillate" },
      { value: "Wine", label: "Wine (vermouth / aperitif base)" },
      { group: "Cane" },
      { value: "High-proof Rum Distillate", label: "High-Proof Rum Distillate" },
      { value: "Light Rum Base", label: "Light Rum Base (column-distilled)" },
      { value: "Pot-still Rum Distillate", label: "Pot-Still Rum Distillate" },
      { group: "Agave" },
      { value: "Tequila Blanco", label: "Tequila Blanco" },
      { value: "Mezcal Distillate", label: "Mezcal Distillate" },
    ],

    prohibitedKeywords: ["sassafras","safrole","calamus","ephedra","ma huang","comfrey","symphytum","pennyroyal","aristolochic","aristolochia","chaparral","germander","teucrium","belladonna","nightshade","henbane","lobelia","yohimbe","oleander","jimsonweed","datura","aconite","tonka bean"],

    formulaPromptContext: `You are an expert master distiller, TTB compliance specialist, and craft spirits formulation expert.
Design a complete, commercially-viable craft spirits formula. The formula must comply with TTB regulations and use only FDA-GRAS approved ingredients.
Include: base spirit, core ingredients, botanicals, colorings, sweeteners, stabilizers, commercial flavoring options, TTB category, COLA guidance, target ABV, flavor notes, appearance, and retail pricing suggestion.`,

    safetyPromptContext: `Review these ingredients for a distilled spirits formula against:
1. FDA's Poisonous Plant Database and non-GRAS substances
2. FDA-banned botanicals (sassafras/safrole, calamus, ephedra, comfrey, pennyroyal, aristolochic acid, etc.)
3. TTB-prohibited or restricted ingredients
4. Thujone, safrole, pulegone, pyrrolizidine alkaloid concerns
5. Ingredients requiring special TTB COLA disclosure`,

    productionContext: "craft distillery production for a licensed distillery",

    marketPromptContext: `You are a US spirits industry market analyst. Analyze competing products for this spirit formula. Focus on craft distilleries, regional and national brands, TTB category competitors, and premium positioning opportunities.`,

    recipePromptContext: `You are a master mixologist. Create signature cocktails featuring this spirit. Include one easy tasting room serve and one sophisticated bartender cocktail.`,
  },

  // ══ CRAFT BREWING ═══════════════════════════════════════════════════════════
  brewing: {
    id: "brewing",
    name: "Craft Brewing",
    icon: "🍺",
    color: "#D4943A",
    tagline: "IPAs, stouts, lagers, sours & beyond",
    description: "AI-powered beer recipe formulation with TTB Brewer's Notice compliance, grain bill design, hop scheduling, and yeast selection for craft breweries.",
    regulatoryBody: "TTB (Brewer's Notice) & FDA (Food Safety)",
    regulatoryShort: "TTB / FDA",
    expertTitle: "master brewer and TTB Brewer's Notice compliance specialist",
    inputLabel: "Beer Style or Flavor Profile",
    inputPlaceholder: '"Hazy New England IPA with tropical fruit and soft bitterness…" or list your desired character',
    outputLabel: "Recipe",
    tab5Label: "Beer Cocktails",
    tab1Label: "Recipe Design Studio",
    categoryLabel: "Beer Style",
    baseLabel: "Base Malt",

    categories: [
      { value: "", label: "— Let AI suggest best style —" },
      { group: "Ales — Hoppy" },
      { value: "American IPA", label: "American IPA" },
      { value: "New England / Hazy IPA", label: "New England / Hazy IPA" },
      { value: "Double / Imperial IPA", label: "Double / Imperial IPA" },
      { value: "West Coast IPA", label: "West Coast IPA" },
      { value: "Session IPA", label: "Session IPA" },
      { value: "American Pale Ale", label: "American Pale Ale" },
      { value: "English IPA", label: "English IPA" },
      { group: "Ales — Dark" },
      { value: "American Stout", label: "American Stout" },
      { value: "Imperial Stout", label: "Imperial Stout" },
      { value: "Milk / Sweet Stout", label: "Milk / Sweet Stout" },
      { value: "Porter", label: "Porter" },
      { value: "English Brown Ale", label: "English Brown Ale" },
      { group: "Ales — Wheat & Belgian" },
      { value: "American Wheat", label: "American Wheat" },
      { value: "Hefeweizen", label: "Hefeweizen" },
      { value: "Belgian Witbier", label: "Belgian Witbier" },
      { value: "Belgian Saison", label: "Belgian Saison / Farmhouse" },
      { value: "Belgian Tripel", label: "Belgian Tripel" },
      { value: "Belgian Dubbel", label: "Belgian Dubbel" },
      { group: "Lagers" },
      { value: "American Lager", label: "American Lager" },
      { value: "Pilsner", label: "Czech / German Pilsner" },
      { value: "Munich Helles", label: "Munich Helles" },
      { value: "Märzen / Oktoberfest", label: "Märzen / Oktoberfest" },
      { value: "Schwarzbier", label: "Schwarzbier (Black Lager)" },
      { group: "Sours & Wild" },
      { value: "American Sour", label: "American Sour / Kettle Sour" },
      { value: "Berliner Weisse", label: "Berliner Weisse" },
      { value: "Gose", label: "Gose" },
      { value: "Lambic / Gueuze", label: "Lambic / Gueuze style" },
      { group: "Specialty" },
      { value: "Fruit Beer", label: "Fruit Beer" },
      { value: "Spiced / Herb Beer", label: "Spiced / Herbed Beer" },
      { value: "Smoked Beer", label: "Smoked Beer" },
      { value: "Barrel-Aged Beer", label: "Barrel-Aged Beer" },
    ],

    bases: [
      { value: "", label: "— Let AI suggest best malt bill —" },
      { group: "American Base Malts" },
      { value: "American 2-Row", label: "American 2-Row Pale Malt" },
      { value: "American 6-Row", label: "American 6-Row Malt" },
      { value: "Pale Ale Malt", label: "Pale Ale Malt" },
      { group: "European Base Malts" },
      { value: "Pilsner Malt", label: "Pilsner Malt (German/Czech)" },
      { value: "Maris Otter", label: "Maris Otter (English)" },
      { value: "Vienna Malt", label: "Vienna Malt" },
      { value: "Munich Malt", label: "Munich Malt" },
      { group: "Wheat & Rye" },
      { value: "Wheat Malt", label: "Wheat Malt" },
      { value: "White Wheat Malt", label: "White Wheat Malt" },
      { value: "Rye Malt", label: "Rye Malt" },
      { group: "Adjuncts" },
      { value: "Flaked Oats", label: "Flaked Oats (haze/body)" },
      { value: "Flaked Wheat", label: "Flaked Wheat" },
      { value: "Corn / Maize Adjunct", label: "Corn / Maize Adjunct" },
      { value: "Rice Adjunct", label: "Rice Adjunct" },
    ],

    prohibitedKeywords: ["unapproved coloring","synthetic caffeine over limit","saccharin","cyclamate"],

    formulaPromptContext: `You are an expert master brewer with deep knowledge of TTB Brewer's Notice requirements, FDA food safety regulations, and the US craft brewing industry.
Design a complete, commercially-viable craft beer recipe. Include: grain bill with percentages, hop schedule (additions, alpha acid %, timing), yeast strain recommendation, target OG/FG/ABV/IBU/SRM, water chemistry profile, adjuncts/specialty ingredients, fermentation temperature and timeline, TTB compliance notes, packaging recommendations, and retail pricing.
Format ingredients per barrel (31 gallons) unless batch size specifies otherwise.`,

    safetyPromptContext: `Review these ingredients for a craft beer recipe against:
1. FDA GRAS status for food/beverage ingredients
2. TTB Brewer's Notice approved adjuncts and additives
3. FDA Food Safety Modernization Act (FSMA) requirements
4. Any ingredients requiring pre-approval or disclosure
5. Allergen labeling requirements (wheat, barley gluten)
6. Caffeine and stimulant addition regulations`,

    productionContext: "craft brewery production for a licensed brewery",

    marketPromptContext: `You are a US craft beer market analyst. Analyze competing beers for this recipe. Focus on craft brewery competitors, regional craft brands, style category leaders, and positioning in the competitive tap handle market.`,

    recipePromptContext: `You are a culinary expert specializing in beer and food pairing. Create 2 signature food pairings and one cocktail or beer cocktail (shandy, black and tan, etc.) featuring this beer. Include one casual tasting room pairing and one upscale restaurant menu pairing.`,
  },

  // ══ WINE & CIDER ════════════════════════════════════════════════════════════
  wine: {
    id: "wine",
    name: "Wine & Cider",
    icon: "🍷",
    color: "#8B3A62",
    tagline: "Wines, meads, ciders & fruit wines",
    description: "AI-powered wine and cider formulation with TTB Bonded Winery compliance, varietal blending, fermentation protocols, and appellation guidance.",
    regulatoryBody: "TTB (Bonded Winery / Cidery Permit)",
    regulatoryShort: "TTB",
    expertTitle: "master winemaker and TTB Bonded Winery compliance specialist",
    inputLabel: "Wine Style or Flavor Profile",
    inputPlaceholder: '"Elegant red blend, dark fruit, cedar, long finish…" or describe your vision',
    outputLabel: "Blend Formula",
    tab5Label: "Food & Pairing",
    tab1Label: "Blend Studio",
    categoryLabel: "Wine Type",
    baseLabel: "Primary Varietal / Base",

    categories: [
      { value: "", label: "— Let AI suggest best style —" },
      { group: "Red Wines" },
      { value: "Cabernet Sauvignon", label: "Cabernet Sauvignon" },
      { value: "Merlot", label: "Merlot" },
      { value: "Pinot Noir", label: "Pinot Noir" },
      { value: "Zinfandel", label: "Zinfandel" },
      { value: "Syrah / Shiraz", label: "Syrah / Shiraz" },
      { value: "Malbec", label: "Malbec" },
      { value: "Red Blend", label: "Red Blend" },
      { group: "White Wines" },
      { value: "Chardonnay", label: "Chardonnay" },
      { value: "Sauvignon Blanc", label: "Sauvignon Blanc" },
      { value: "Pinot Grigio / Gris", label: "Pinot Grigio / Pinot Gris" },
      { value: "Riesling", label: "Riesling" },
      { value: "Gewurztraminer", label: "Gewurztraminer" },
      { value: "White Blend", label: "White Blend" },
      { group: "Rosé & Sparkling" },
      { value: "Rosé", label: "Rosé" },
      { value: "Sparkling Wine", label: "Sparkling Wine (Méthode)" },
      { value: "Pétillant Naturel", label: "Pétillant Naturel (Pét-Nat)" },
      { group: "Specialty" },
      { value: "Dessert Wine", label: "Dessert / Late Harvest Wine" },
      { value: "Fortified Wine", label: "Fortified Wine (Port style)" },
      { value: "Orange Wine", label: "Orange / Skin-Contact Wine" },
      { value: "Fruit Wine", label: "Fruit Wine (non-grape)" },
      { group: "Cider & Mead" },
      { value: "Hard Cider", label: "Hard Cider (Apple)" },
      { value: "Perry", label: "Perry (Pear Cider)" },
      { value: "Flavored Cider", label: "Flavored Hard Cider" },
      { value: "Traditional Mead", label: "Traditional Mead" },
      { value: "Melomel", label: "Melomel (Fruit Mead)" },
      { value: "Cyser", label: "Cyser (Apple Mead)" },
      { value: "Metheglin", label: "Metheglin (Spiced Mead)" },
    ],

    bases: [
      { value: "", label: "— Let AI suggest best varietal blend —" },
      { group: "Red Varietals" },
      { value: "Cabernet Sauvignon", label: "Cabernet Sauvignon" },
      { value: "Merlot", label: "Merlot" },
      { value: "Pinot Noir", label: "Pinot Noir" },
      { value: "Zinfandel", label: "Zinfandel" },
      { value: "Syrah", label: "Syrah" },
      { group: "White Varietals" },
      { value: "Chardonnay", label: "Chardonnay" },
      { value: "Sauvignon Blanc", label: "Sauvignon Blanc" },
      { value: "Riesling", label: "Riesling" },
      { group: "Cider & Mead Bases" },
      { value: "Culinary Apple Blend", label: "Culinary Apple Blend" },
      { value: "Heritage / Bittersweet Apples", label: "Heritage / Bittersweet Apples" },
      { value: "Wildflower Honey", label: "Wildflower Honey" },
      { value: "Varietal Honey", label: "Varietal Honey (clover, orange blossom, etc.)" },
      { value: "Pear (Perry)", label: "Pear / Perry base" },
    ],

    prohibitedKeywords: ["artificial color not approved","diethylene glycol","methanol additive"],

    formulaPromptContext: `You are an expert winemaker, cider maker, or meadmaker with deep knowledge of TTB Bonded Winery/Cidery permit requirements, CFR Title 27 wine regulations, and the US craft wine industry.
Design a complete, commercially-viable wine/cider/mead formula. Include: varietal composition and percentages, fermentation protocol (yeast strain, temperature, duration), maceration details if applicable, fining and stabilization agents, SO2 management, oak treatment if relevant, target alcohol, residual sugar, acidity, pH, appellation considerations, TTB compliance notes, and retail pricing.`,

    safetyPromptContext: `Review these ingredients for a wine/cider/mead formula against:
1. TTB Bonded Winery approved additives and processes (27 CFR Part 24)
2. FDA GRAS status for enological additives
3. Sulfite disclosure requirements (>10ppm must be labeled)
4. Allergen considerations (egg whites, casein, isinglass fining agents)
5. Organic certification requirements if applicable
6. Appellation labeling restrictions`,

    productionContext: "bonded winery, cidery, or meadery production under TTB permit",

    marketPromptContext: `You are a US wine and cider market analyst. Analyze competing products for this wine/cider formula. Focus on regional wineries, craft cideries, price tier positioning, and retail shelf competition.`,

    recipePromptContext: `You are a sommelier and culinary expert. Create 2 signature food pairings and one cocktail or spritz featuring this wine/cider. Include classic pairing guidance and one unexpected creative pairing that would delight customers.`,
  },

  // ══ NON-ALCOHOLIC BEVERAGES ══════════════════════════════════════════════════
  nonalc: {
    id: "nonalc",
    name: "Non-Alcoholic Beverages",
    icon: "🥤",
    color: "#1D9E75",
    tagline: "Functional drinks, NA spirits, sodas & more",
    description: "AI-powered beverage formulation with FDA compliance, GRAS ingredient screening, nutrition label guidance, and market positioning for non-alcoholic brands.",
    regulatoryBody: "FDA (Food Safety Modernization Act / GRAS)",
    regulatoryShort: "FDA",
    expertTitle: "beverage formulation scientist with deep FDA food safety and labeling expertise",
    inputLabel: "Beverage Concept or Flavor Profile",
    inputPlaceholder: '"Adaptogenic sparkling water with ashwagandha, citrus, light sweetness…" or describe your concept',
    outputLabel: "Formula",
    tab5Label: "Serve Suggestions",
    tab1Label: "Formula Studio",
    categoryLabel: "Beverage Category",
    baseLabel: "Base / Format",

    categories: [
      { value: "", label: "— Let AI suggest best category —" },
      { group: "Functional & Wellness" },
      { value: "Adaptogenic Beverage", label: "Adaptogenic Beverage" },
      { value: "Nootropic Drink", label: "Nootropic / Cognitive Drink" },
      { value: "Energy Drink", label: "Energy Drink" },
      { value: "Immunity / Wellness Shot", label: "Immunity / Wellness Shot" },
      { value: "Electrolyte Drink", label: "Electrolyte / Hydration Drink" },
      { value: "Probiotic Beverage", label: "Probiotic Beverage" },
      { group: "Non-Alcoholic Spirits & Mixers" },
      { value: "NA Spirit Alternative", label: "Non-Alcoholic Spirit Alternative" },
      { value: "NA Aperitif", label: "Non-Alcoholic Aperitif" },
      { value: "Cocktail Mixer", label: "Premium Cocktail Mixer" },
      { value: "Shrub / Drinking Vinegar", label: "Shrub / Drinking Vinegar" },
      { group: "Carbonated" },
      { value: "Craft Soda", label: "Craft Soda" },
      { value: "Sparkling Water", label: "Sparkling / Flavored Water" },
      { value: "Kombucha", label: "Kombucha" },
      { value: "Water Kefir", label: "Water Kefir" },
      { group: "Juice & Tea" },
      { value: "Cold Press Juice", label: "Cold Press Juice / Blend" },
      { value: "Botanical Tea RTD", label: "Botanical Tea RTD" },
      { value: "Switchel", label: "Switchel / Drinking Vinegar" },
      { group: "Cannabis / Hemp" },
      { value: "CBD Beverage", label: "CBD / Hemp-Derived Beverage" },
      { value: "Delta-9 THC Beverage", label: "Delta-9 THC Beverage (state-compliant)" },
    ],

    bases: [
      { value: "", label: "— Let AI suggest best base —" },
      { group: "Water Bases" },
      { value: "Purified Still Water", label: "Purified Still Water" },
      { value: "Carbonated Water (CO2)", label: "Carbonated Water (CO2 injected)" },
      { value: "Mineral Water", label: "Natural Mineral Water" },
      { group: "Juice & Tea Bases" },
      { value: "Apple Juice Base", label: "Apple Juice Base" },
      { value: "White Grape Juice Base", label: "White Grape Juice Base" },
      { value: "Green Tea Base", label: "Green Tea Base" },
      { value: "Black Tea Base", label: "Black Tea Base" },
      { value: "Kombucha SCOBY Culture", label: "Kombucha SCOBY Culture" },
      { group: "Botanical Distillate" },
      { value: "Botanical Hydrosol", label: "Botanical Hydrosol / Distillate" },
      { value: "CO2 Botanical Extract", label: "CO2 Botanical Extract Base" },
      { group: "Vinegar Bases" },
      { value: "Apple Cider Vinegar", label: "Apple Cider Vinegar (raw)" },
      { value: "White Wine Vinegar", label: "White Wine Vinegar" },
    ],

    prohibitedKeywords: ["ephedra","ma huang","aristolochic","pennyroyal oil","comfrey","kava","dmaa","dmha","synephrine","stimulant not gras","unapproved color additive"],

    formulaPromptContext: `You are an expert beverage formulation scientist with deep knowledge of FDA food safety regulations, GRAS ingredient status, FDA labeling requirements (21 CFR), and the US functional beverage market.
Design a complete, commercially-viable non-alcoholic beverage formula. Include: base, active/functional ingredients with dosages, flavor system (natural flavors, fruit juices, botanicals), sweetener system, acidulant, preservatives if needed, carbonation level, target Brix/pH/water activity, Nutrition Facts panel estimates, ingredient declaration order, FDA compliance notes, shelf life estimate, and retail pricing.
All ingredients must be FDA-GRAS approved for use in beverages. Note any ingredients requiring specific label disclosures.`,

    safetyPromptContext: `Review these ingredients for a non-alcoholic beverage formula against:
1. FDA GRAS (Generally Recognized as Safe) status for beverage use
2. FDA-prohibited or restricted substances in food/beverages
3. Dietary supplement vs food ingredient classification issues
4. Allergen labeling requirements (FDA Top 9 allergens)
5. Color additives requiring FDA certification
6. Caffeine levels and required disclosures
7. Structure/function claim restrictions for food products
8. Any ingredients with FDA warning letters or import alerts`,

    productionContext: "FDA-regulated beverage manufacturing facility",

    marketPromptContext: `You are a US functional beverage market analyst. Analyze competing products for this non-alcoholic beverage formula. Focus on emerging functional beverage brands, NA spirits competitors, retail channel positioning (Whole Foods, Target, DTC), and premium pricing opportunities.`,

    recipePromptContext: `You are a beverage mixologist specializing in non-alcoholic cocktails and serves. Create 2 signature serve suggestions featuring this beverage — one simple everyday serve and one elevated mocktail or mixed serve for bar programs and special occasions.`,
  },

  // ══ SUPPLEMENTS & NUTRACEUTICALS ════════════════════════════════════════════
  supplements: {
    id: "supplements",
    name: "Supplements & Nutraceuticals",
    icon: "💊",
    color: "#378ADD",
    tagline: "Capsules, powders, gummies & functional formulas",
    description: "AI-powered supplement formulation with FDA DSHEA compliance, structure/function claim guidance, ingredient safety screening, and GMP manufacturing protocols.",
    regulatoryBody: "FDA (DSHEA — Dietary Supplement Health and Education Act)",
    regulatoryShort: "FDA / DSHEA",
    expertTitle: "nutraceutical formulation scientist with deep FDA DSHEA and GMP compliance expertise",
    inputLabel: "Health Goal or Formula Concept",
    inputPlaceholder: '"Daily immune support with elderberry, zinc, and vitamin C for adults…" or describe your supplement concept',
    outputLabel: "Formula",
    tab5Label: "Usage Guidance",
    tab1Label: "Formula Studio",
    categoryLabel: "Supplement Category",
    baseLabel: "Delivery Format",

    categories: [
      { value: "", label: "— Let AI suggest best category —" },
      { group: "Vitamins & Minerals" },
      { value: "Multivitamin", label: "Multivitamin / Multimineral" },
      { value: "Vitamin D", label: "Vitamin D Supplement" },
      { value: "Vitamin C", label: "Vitamin C Supplement" },
      { value: "B-Complex", label: "B-Complex / B Vitamins" },
      { value: "Magnesium", label: "Magnesium Supplement" },
      { value: "Zinc Supplement", label: "Zinc Supplement" },
      { group: "Immune & Wellness" },
      { value: "Immune Support", label: "Immune Support Formula" },
      { value: "Antioxidant Formula", label: "Antioxidant Formula" },
      { value: "Elderberry Formula", label: "Elderberry / Berry Formula" },
      { value: "Probiotic", label: "Probiotic Supplement" },
      { group: "Sports & Performance" },
      { value: "Pre-Workout", label: "Pre-Workout Formula" },
      { value: "Protein Supplement", label: "Protein Supplement" },
      { value: "BCAAs", label: "BCAA / Amino Acid Formula" },
      { value: "Electrolyte Formula", label: "Electrolyte Formula" },
      { value: "Creatine Formula", label: "Creatine Formula" },
      { group: "Cognitive & Mood" },
      { value: "Nootropic Formula", label: "Nootropic / Cognitive Support" },
      { value: "Stress & Adaptogens", label: "Stress Relief / Adaptogen Formula" },
      { value: "Sleep Formula", label: "Sleep Support Formula" },
      { value: "Mood Support", label: "Mood / Mental Wellness Support" },
      { group: "Beauty & Longevity" },
      { value: "Collagen Supplement", label: "Collagen / Beauty Formula" },
      { value: "Anti-Aging Formula", label: "Anti-Aging / Longevity Formula" },
      { value: "Hair Skin Nails", label: "Hair, Skin & Nails Formula" },
      { group: "Metabolic & Weight" },
      { value: "Metabolic Support", label: "Metabolic Support Formula" },
      { value: "Blood Sugar Support", label: "Blood Sugar Support" },
      { value: "Weight Management", label: "Weight Management Formula" },
    ],

    bases: [
      { value: "", label: "— Let AI suggest best delivery format —" },
      { group: "Oral Solid Dose" },
      { value: "Vegetarian Capsule (HPMC)", label: "Vegetarian Capsule (HPMC)" },
      { value: "Gelatin Capsule", label: "Gelatin Capsule" },
      { value: "Tablet (compressed)", label: "Tablet (compressed)" },
      { value: "Softgel Capsule", label: "Softgel / Liquid-filled Capsule" },
      { group: "Gummies & Chewables" },
      { value: "Pectin Gummy (vegan)", label: "Pectin Gummy (vegan)" },
      { value: "Gelatin Gummy", label: "Gelatin Gummy" },
      { value: "Chewable Tablet", label: "Chewable Tablet" },
      { group: "Powders" },
      { value: "Powder (stick pack)", label: "Powder — Stick Pack" },
      { value: "Powder (tub / scoop)", label: "Powder — Tub / Scoop" },
      { value: "Effervescent Tablet", label: "Effervescent Tablet" },
      { group: "Liquids" },
      { value: "Liquid Tincture", label: "Liquid Tincture / Drops" },
      { value: "Ready-to-Drink Shot", label: "Ready-to-Drink Shot (2oz)" },
      { value: "Syrup", label: "Syrup / Elixir" },
    ],

    prohibitedKeywords: ["ephedra","ma huang","dmaa","dmha","1,3-dimethylbutylamine","aconitine","aristolochic","comfrey","pennyroyal","chaparral","kava","thunder god vine","bitter orange oil","synephrine at high dose","androstenedione","androstenediol","sarms","selective androgen"],

    formulaPromptContext: `You are an expert nutraceutical formulation scientist with deep knowledge of FDA DSHEA regulations, Current Good Manufacturing Practices (cGMP 21 CFR Part 111), structure/function claim requirements, and the US supplement market.
Design a complete, commercially-viable dietary supplement formula. Include: active ingredients with clinically-relevant dosages and forms (e.g., magnesium glycinate vs oxide), excipients, delivery format specifications, serving size and servings per container, Supplement Facts panel, structure/function claims (with required disclaimer), GMP manufacturing notes, third-party testing recommendations, stability considerations, and retail pricing.
All ingredients must comply with DSHEA. Flag any New Dietary Ingredient (NDI) notification requirements. Do NOT include any drug claims.`,

    safetyPromptContext: `Review these supplement ingredients against:
1. FDA DSHEA safety requirements — is each ingredient a legitimate dietary ingredient?
2. FDA-prohibited substances in dietary supplements (ephedra, DMAA, DMHA, SARMs, anabolic steroids, etc.)
3. New Dietary Ingredient (NDI) notification requirements (ingredients introduced after Oct 15, 1994)
4. Proprietary blend disclosure requirements
5. Drug-supplement interaction concerns for key ingredients
6. Upper tolerable intake levels (ULs) from Institute of Medicine / NIH
7. Allergen labeling (soy, milk, wheat, tree nuts in excipients)
8. Prop 65 (California) considerations for heavy metals in botanical ingredients`,

    productionContext: "FDA-registered dietary supplement manufacturing facility following cGMP (21 CFR Part 111)",

    marketPromptContext: `You are a US dietary supplement market analyst. Analyze competing products for this supplement formula. Focus on Amazon bestsellers, specialty retail (Whole Foods, Vitamin Shoppe, Sprouts), DTC supplement brands, and clinical/professional channel opportunities.`,

    recipePromptContext: `You are a wellness and nutrition expert. Create 2 usage suggestions for this supplement — how to incorporate it into a daily routine, what to stack it with for best results, and any lifestyle recommendations that enhance its effectiveness.`,
  },
};

// ── Helper: build optgroups from flat array with {group} markers ──────────────
export function buildOptgroups(list) {
  const els = []; let grp = null; let opts = [];
  const flush = () => { if (grp) { els.push({ type: "group", label: grp, options: opts }); opts = []; grp = null; } };
  list.forEach(item => {
    if (item.group) { flush(); grp = item.group; }
    else { opts.push(item); }
  });
  flush();
  return els;
}

// ── Helper: get all verticals as array ────────────────────────────────────────
export const VERTICAL_LIST = Object.values(VERTICALS);

// ── Helper: build the PROHIBITED scrub function for a vertical ────────────────
export function buildIsBanned(vertical) {
  return (name) => {
    if (!name) return false;
    const lower = name.toLowerCase();
    return vertical.prohibitedKeywords.some(kw => lower.includes(kw));
  };
}
// updated Mon May 18 21:16:45 EDT 2026
// cache bust Fri May 22 16:51:11 EDT 2026
